import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { getPendingGiftRedirect } from "@/utils/pendingGiftRedirect";
import { getPendingEventRedirect } from "@/utils/pendingEventBooking";

interface AuthRedirectProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

export function AuthRedirect({
  children,
  requireAuth = false,
  redirectTo = "/login",
}: AuthRedirectProps) {
  const { user, userRole, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't redirect while loading
    if (isLoading) return;

    // If auth is required but no user, redirect to login
    if (requireAuth && !user) {
      console.log(
        "AuthRedirect - Authentication required, redirecting to:",
        redirectTo,
      );
      navigate(redirectTo, { replace: true });
      return;
    }

    // If user is logged in, check for pending gift redirect first
    if (user && userRole) {
      const pendingRedirect =
        getPendingGiftRedirect() || getPendingEventRedirect();
      if (pendingRedirect) {
        console.log("AuthRedirect - Pending redirect:", pendingRedirect);
        navigate(pendingRedirect, { replace: true });
        return;
      }
      // Only redirect to dashboard if on login page
      if (window.location.pathname === "/login") {
        const destination = `/dashboard/${userRole}`;
        console.log(
          "AuthRedirect - User already logged in, redirecting to:",
          destination,
        );
        navigate(destination, { replace: true });
        return;
      }
    }
  }, [user, userRole, isLoading, requireAuth, redirectTo, navigate]);

  // Show loading while auth state is being determined
  if (isLoading) {
    return <LoadingScreen message="Checking authentication…" />;
  }

  // If auth is required but no user, don't render children
  if (requireAuth && !user) {
    return null;
  }

  return <>{children}</>;
}
